import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { HdContentLanguageService } from '../bodygraph/hd-content-language.service';
import { HdGateInfoService } from '../bodygraph/hd-gate-info.service';

/**
 * Circuit-indicatorkleur per angst-poort, gelijk aan de circuit-source-of-truth
 * (`HD_BODYGRAPH_CIRCUITS` in `hd-bodygraph-circuit-metadata.ts`): collectief-
 * logisch #05A1F9, collectief-abstract #416CAC, individueel-weten #00C165,
 * tribaal-ondernemend/-community #F01C1C, tribaal-defensief #F27A39. Poort 57
 * zit in twee individuele circuits en krijgt een split-rail (weten + integratie).
 */
export const HD_FEAR_GATE_COLOR: Readonly<Record<number, string>> = {
  4: '#05A1F9', 17: '#05A1F9', 48: '#05A1F9', 18: '#05A1F9',
  47: '#416CAC', 11: '#416CAC', 36: '#416CAC', 30: '#416CAC',
  24: '#00C165', 43: '#00C165', 28: '#00C165', 22: '#00C165', 55: '#00C165', 57: '#00C165',
  44: '#F01C1C', 32: '#F01C1C', 37: '#F01C1C', 49: '#F01C1C',
  50: '#F27A39', 6: '#F27A39',
};

/** Tweede indicatorkleur (split-rail) voor poorten in twee circuits. */
export const HD_FEAR_GATE_COLOR_2: Readonly<Record<number, string>> = {
  57: '#00522B', // individueel-integratie naast individueel-weten
};

interface FearCardVm {
  readonly rail: string;
  readonly prefix: string;
  readonly title: string;
  readonly life: string;
  readonly mood: string;
}

/**
 * Het angst-"blokje" van de Bewustzijn & angsten cheat sheet als zelfstandig,
 * overal inzetbaar component: poortnummer, angst-prefix + -titel, not-self-
 * levensthema en (enkel Solar Plexus) het emotionele thema, met de circuit-
 * indicatorkleur als linker rail. Leest zijn content zelf uit de
 * `human-design/awareness`-scope (taalwissel + NL-fallback inbegrepen), zodat
 * de kaart zowel op de cheat sheet als in info-panelen identiek oogt.
 */
@Component({
  selector: 'hd-fear-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'fear-card',
    '[class.fear-card--compact]': "size() === 'compact'",
    '[attr.data-gate]': 'gate()',
  },
  template: `
    <span class="fear-card__rail" [style.background]="vm().rail"></span>
    <span class="fear-card__gate">{{ gate() }}</span>
    <div class="fear-card__body">
      <p class="fear-card__prefix">{{ vm().prefix }}</p>
      <p class="fear-card__title">{{ vm().title }}</p>
    </div>
    <div class="fear-card__meta">
      <p class="fear-card__life">{{ vm().life }}</p>
      @if (vm().mood) {
        <p class="fear-card__mood">({{ vm().mood }})</p>
      }
    </div>
  `,
  styles: `
    :host {
      position: relative;
      display: flex;
      align-items: center;
      gap: 12px;
      background: var(--white);
      border: 1px solid #e6e6e6;
      border-radius: 6px;
      padding: 8px 14px 8px 18px;
      color: #3d414e;
      text-align: left;
    }

    .fear-card__rail {
      position: absolute;
      left: 0;
      top: 5px;
      bottom: 5px;
      width: 5px;
      border-radius: 3px;
    }

    .fear-card__gate {
      flex: 0 0 auto;
      min-width: 40px;
      text-align: center;
      font-family: 'Bw Modelica', 'Raleway', sans-serif;
      font-weight: 900;
      font-size: 26px;
      line-height: 1;
    }

    .fear-card__body {
      flex: 1 1 auto;
      min-width: 0;
    }

    .fear-card__prefix {
      margin: 0;
      font-size: 12px;
      color: rgba(61, 65, 78, 0.7);
    }

    .fear-card__title {
      margin: 1px 0 0;
      font-weight: 800;
      font-size: 15.5px;
      line-height: 1.2;
    }

    .fear-card__meta {
      flex: 0 0 auto;
      max-width: 42%;
      text-align: right;
    }

    .fear-card__life {
      margin: 0;
      font-size: 11.5px;
      color: rgba(61, 65, 78, 0.55);
    }

    .fear-card__mood {
      margin: 2px 0 0;
      font-weight: 800;
      font-size: 13px;
    }

    /* Compacte variant voor smalle panelen: zelfde opbouw, kleinere maten. */
    :host(.fear-card--compact) {
      gap: 8px;
      padding: 6px 10px 6px 14px;
    }

    :host(.fear-card--compact) .fear-card__gate {
      min-width: 30px;
      font-size: 20px;
    }

    :host(.fear-card--compact) .fear-card__prefix {
      font-size: 10.5px;
    }

    :host(.fear-card--compact) .fear-card__title {
      font-size: 13.5px;
    }

    :host(.fear-card--compact) .fear-card__life {
      font-size: 10px;
    }

    :host(.fear-card--compact) .fear-card__mood {
      font-size: 11.5px;
    }
  `,
})
export class HdFearCardComponent {
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly contentLang = inject(HdContentLanguageService);

  readonly gate = input.required<number>();
  /** `compact` maakt het blokje smaller-vriendelijk voor gedockte panelen. */
  readonly size = input<'default' | 'compact'>('default');

  protected readonly vm = computed<FearCardVm>(() => {
    const gate = this.gate();
    const color = HD_FEAR_GATE_COLOR[gate] ?? '#3d414e';
    const color2 = HD_FEAR_GATE_COLOR_2[gate];
    return {
      rail: color2 ? `linear-gradient(180deg, ${color2} 50%, ${color} 50%)` : color,
      prefix: this.text(`gates.${gate}.prefix`),
      title: this.text(`gates.${gate}.title`),
      life: this.text(`gates.${gate}.life`),
      mood: this.text(`gates.${gate}.mood`),
    };
  });

  /** Scope-tekst in de content-taal, met NL-fallback voor (nog) lege EN-velden. */
  private text(key: string): string {
    const lang = this.contentLang.effectiveLang();
    const value = this.gateInfo.cheatSheetTextIn('awareness', key, lang);
    if (value !== '' || lang === 'nl') {
      return value;
    }
    return this.gateInfo.cheatSheetTextIn('awareness', key, 'nl');
  }
}
